import { createPortal } from 'react-dom';
import { useState } from 'react';
import { useTranslations } from '../../i18n';
import { MessageSquare, Send, X } from 'lucide-react';

interface FeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (feedback: string) => void;
    title?: string;
    message?: string;
}

export function FeedbackModal({
    isOpen,
    onClose,
    onSubmit,
    title,
    message,
}: FeedbackModalProps) {
    const { t } = useTranslations();
    const [feedback, setFeedback] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    if (!isOpen) return null;

    const modalTitle = title || (t.landing.pricing as any).upgradePopup.title;
    const modalMessage = message || (t.landing.pricing as any).upgradePopup.message;

    const handleInternalSubmit = () => {
        onSubmit(feedback);
        setIsSubmitted(true);
    };

    const handleClose = () => {
        setFeedback('');
        setIsSubmitted(false);
        onClose();
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={handleClose}
            />
            <div className="relative bg-white dark:bg-[#00172D] rounded-[2.5rem] shadow-2xl w-full max-w-lg p-8 transform transition-all scale-100 animate-in fade-in zoom-in-95 duration-200 border border-transparent dark:border-white/5">
                {!isSubmitted ? (
                    <>
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-3xl flex items-center justify-center text-blue-600 dark:text-blue-400 mx-auto mb-6">
                                <MessageSquare size={32} />
                            </div>
                            <h3 className="text-3xl font-black text-gray-900 dark:text-white mb-4">{modalTitle}</h3>
                            <p className="text-gray-500 dark:text-gray-400 font-medium leading-relaxed">
                                {modalMessage}
                            </p>
                        </div>

                        <div className="mb-8">
                            <textarea
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                placeholder={(t.landing.pricing as any).upgradePopup.placeholder}
                                className="w-full h-32 px-5 py-4 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all resize-none"
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleClose}
                                className="flex-1 py-4 px-6 rounded-2xl font-bold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 transition-all active:scale-95"
                            >
                                {(t.landing.pricing as any).upgradePopup.cancel}
                            </button>
                            <button
                                onClick={handleInternalSubmit}
                                className="flex-1 py-4 px-6 rounded-2xl font-bold text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 shadow-lg shadow-blue-500/20 dark:shadow-none transition-all active:scale-95 flex items-center justify-center gap-2"
                            >
                                {(t.landing.pricing as any).upgradePopup.submit}
                                <Send size={18} />
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-8">
                        <div className="w-20 h-20 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center text-green-600 dark:text-green-400 mx-auto mb-8">
                            <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3 className="text-3xl font-black text-gray-900 dark:text-white mb-4">
                            {(t.landing.pricing as any).upgradePopup.thanksTitle}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 font-medium leading-relaxed mb-10">
                            {(t.landing.pricing as any).upgradePopup.thanksMessage}
                        </p>
                        <button
                            onClick={handleClose}
                            className="w-full py-4 px-6 rounded-2xl font-bold text-white bg-green-600 hover:bg-green-700 dark:bg-green-500 shadow-lg shadow-green-500/20 dark:shadow-none transition-all active:scale-95"
                        >
                            {(t.landing.pricing as any).upgradePopup.thanksOk}
                        </button>
                    </div>
                )}
                
                <button 
                    onClick={handleClose}
                    className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                >
                    <X size={24} />
                </button>
            </div>
        </div>,
        document.body
    );
}
